'use client';

import { useState, useEffect } from 'react';
import { Bell, Clock, ExternalLink, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { isDemoMode } from '@/lib/demo-mode';
import { getDoorLabel } from '@/lib/door/displayLabel';

interface DamageReport {
  id: string;
  door_id: string;
  description: string;
  created_at: string;
  workflow_stage: string;
  // Hierarchie-Info
  customer_name: string;
  location_name: string;
  // Anzeige-Feld vom Door-Join
  door_number: string | null;
}

interface NewDamageReportsProps {
  demoMode?: boolean;
}

export default function NewDamageReports({ demoMode = false }: NewDamageReportsProps) {
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchNewDamageReports = async () => {
      try {
        // Query für alle Reparaturen mit workflow_stage = 'schadensmeldung'
        let query = supabase
          .from('repairs')
          .select(`
            id,
            door_id,
            description,
            created_at,
            workflow_stage,
            delivery_location_id,
            doors!inner(
              door_number
            ),
            delivery_locations!inner(
              id,
              location_name,
              customers!inner(
                id,
                company_name,
                is_demo
              )
            )
          `)
          .eq('workflow_stage', 'schadensmeldung')
          .order('created_at', { ascending: false });

        const { data: repairs, error } = await query;

        if (error) {
          console.error('Fehler beim Laden der Schadensmeldungen:', error);
          setLoading(false);
          return;
        }

        if (!repairs) {
          setLoading(false);
          return;
        }

        // Demo-Mode-Filter anwenden
        const filteredRepairs = repairs.filter((repair: any) => {
          const isDemo = repair.delivery_locations?.customers?.is_demo === true;
          return demoMode ? isDemo : !isDemo;
        });

        // Mapping zu DamageReport-Interface
        const reports: DamageReport[] = filteredRepairs.map((repair: any) => ({
          id: repair.id,
          door_id: repair.door_id,
          description: repair.description || 'Keine Beschreibung',
          created_at: repair.created_at,
          workflow_stage: repair.workflow_stage,
          customer_name: repair.delivery_locations?.customers?.company_name || 'Unbekannter Kunde',
          location_name: repair.delivery_locations?.location_name || 'Unbekannter Ort',
          door_number: repair.doors?.door_number ?? null
        }));

        // Demo-Daten falls keine echten Schadensmeldungen
        if (demoMode && reports.length === 0) {
          reports.push(
            {
              id: 'demo-damage-1',
              door_id: 'demo-door-1',
              description: 'Türschließer defekt - sofortige Reparatur erforderlich',
              created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
              workflow_stage: 'schadensmeldung',
              customer_name: 'Pflegeheim Sonnenschein',
              location_name: 'Hauptgebäude',
              door_number: 'T012'
            },
            {
              id: 'demo-damage-2',
              door_id: 'demo-door-2',
              description: 'Brandschutztür - Schließmechanismus beschädigt',
              created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
              workflow_stage: 'schadensmeldung',
              customer_name: 'Industriepark Harburg',
              location_name: 'Gebäude C',
              door_number: 'T005'
            }
          );
        }

        setDamageReports(reports);
        setLoading(false);

      } catch (error) {
        console.error('Fehler beim Laden der Schadensmeldungen:', error);
        setLoading(false);
      }
    };

    fetchNewDamageReports();
  }, [demoMode]);

  // Nicht ausgeblendete Reports filtern
  const visibleReports = damageReports.filter(report => !dismissed.includes(report.id));

  // Anzuzeigende Reports (limitiert oder alle)
  const displayedReports = showAll ? visibleReports : visibleReports.slice(0, 3);

  // Keine Reports = Component nicht anzeigen
  if (loading || visibleReports.length === 0) {
    return null;
  }

  const handleDismiss = (reportId: string) => {
    setDismissed(prev => [...prev, reportId]);
  };

  const getDaysAgo = (dateString: string) => {
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'heute';
    if (days === 1) return 'gestern';
    return `vor ${days} Tagen`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-lg p-4 flex-1 max-w-lg mb-6">
      <div className="flex items-start">
        <Bell className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Neue Schadensmeldungen
          </h3>
          <p className="text-sm text-gray-700 mb-4">
            {visibleReports.length} neue Schadensmeldung{visibleReports.length !== 1 ? 'en' : ''} - bitte Angebot erstellen
          </p>

          <div className="space-y-3">
            {displayedReports.map((report) => (
              <div key={report.id} className="bg-white rounded-lg p-3 border border-red-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <Clock className="h-4 w-4 mr-1" />
                      {getDaysAgo(report.created_at)} ({formatDate(report.created_at)})
                    </div>

                    <p className="font-medium text-gray-900 text-sm mb-1 break-words">
                      {report.customer_name} • {report.location_name} • Tür {getDoorLabel({ door_number: report.door_number })}
                    </p>

                    <p className="text-sm text-gray-700 break-words overflow-hidden">
                      {report.description}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <a
                      href={`/reparatur-management/${report.id}`}
                      className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full hover:bg-red-200 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Angebot erstellen
                    </a>

                    <button
                      onClick={() => handleDismiss(report.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Benachrichtigung ausblenden"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {visibleReports.length > 3 && (
            <div className="mt-3 text-center">
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-sm text-gray-600 hover:text-gray-800 font-medium"
              >
                {showAll
                  ? 'Weniger anzeigen'
                  : `${visibleReports.length - 3} weitere Meldungen anzeigen`
                }
              </button>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-red-200">
            <p className="text-xs text-gray-600">
              Klicken Sie auf "Angebot erstellen" um den Reparatur-Workflow zu starten.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
